import { createModel } from '@rematch/core';
import http from '../http/http-common';
import { generatePath } from 'react-router-dom';
import URL from '../../constants/api-constants';
import { saveAs } from 'file-saver';
import type { RootModel } from './index';
import { DataZoomEvent } from '../../components/CycleChartPreview/types';
import { ICustomChannelName } from '../../types/channel-types';
import { ReportType } from '../../types/report-type-enum';
import { BaseApi } from '../http/base-api.service';
import { ControllerItemType } from '../../enums/controller-item-type';
import { toast } from 'react-toastify';
import { AxiosError, AxiosRequestConfig, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';

export interface ReportScreenshot {
  id: string;
  created_at: string;
  updated_at: string;
  url: string;
  type: ReportType;
  order: number;
  cycleReportId: string;
  isIncluded: boolean;
  image: string;
}

export interface CycleReportItem {
  id: string;
  name: string;
  color: string;
  excluded: boolean;
  cycleReportId: string;
  type: ControllerItemType;
  itemNumber: number;
  controllerId: string;
  controller_serial_number: string;
}

interface ReportsState {
  reports: ReportScreenshot[];
  pdfReports: ReportScreenshot[];
  reportItems: CycleReportItem[] | null;
  mainScreenshot: string;
  loading: boolean;
  mainScreenLoading: boolean;
  downloading: boolean;
  error: string | null;
}

export interface ITakeGraphScreenshotProps {
  cycleId: string;
  dataZoom: DataZoomEvent;
  screenshot: Blob;
  previewScreenshot?: Blob;
  timeLine: { from: number; to: number };
  channels: string[];
}

export interface ITakeMainScreenshotProps {
  cycleId: string;
  screenshot: Blob;
}

const apiService = new BaseApi();
const headers: AxiosRequestConfig['headers'] = apiService.getApiVersionHeader();

const getFilenameFromHeaders = (headers: AxiosResponseHeaders | Partial<RawAxiosResponseHeaders>): string =>  {
  const DEFAULT_HEADER_NAME = 'report.pdf';

  const contentDisposition = headers['content-disposition'];

  if (!contentDisposition) return DEFAULT_HEADER_NAME;

  const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n\r]+)["']?/i);

  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      console.error(error);
      return DEFAULT_HEADER_NAME;
    }
  }

  return DEFAULT_HEADER_NAME;
}

// generatePdf/generatePdfV2 request responseType: 'blob' so a successful PDF
// downloads correctly, but that also makes axios deliver a JSON error body
// (e.g. the 409 CONTROLLERS_MISSING_VALID_CALCERT response) as a Blob
// instead of a parsed object. Replace error.response.data with the parsed
// JSON in place so callers can inspect it normally (e.g. getMissingCalcertError).
const parseBlobErrorResponse = async (error: unknown): Promise<void> => {
  if (!(error instanceof AxiosError) || !(error.response?.data instanceof Blob)) {
    return;
  }

  try {
    const text = await error.response.data.text();
    error.response.data = JSON.parse(text);
  } catch {
    // Not JSON - leave the blob as-is.
  }
};

export const reports = createModel<RootModel>()({
  state: {
    reports: [],
    pdfReports: [],
    reportItems: null,
    loading: false,
    mainScreenLoading: false,
    mainScreenshot: '',
    downloading: false,
    error: 'null',
  } as ReportsState,

  reducers: {
    setReports(state, payload: ReportScreenshot[]) {
      return { ...state, reports: payload };
    },
    setPdfReports(state, payload: ReportScreenshot[]) {
      return { ...state, pdfReports: payload };
    },
    setMainScreenshot(state, payload: string) {
      return { ...state, mainScreenshot: payload };
    },
    setLoading(state, payload: boolean) {
      return { ...state, loading: payload };
    },
    setMainScreenLoading(state, payload: boolean) {
      return { ...state, mainScreenLoading: payload };
    },
    setDownloading(state, payload: boolean) {
      return { ...state, downloading: payload };
    },
    setError(state, payload: string | null) {
      return { ...state, error: payload };
    },
    setReportItems(state, payload: CycleReportItem[]) {
      return { ...state, reportItems: payload };
    },
  },

  effects: (dispatch) => ({
    async fetchReports(cycleId: string) {
      try {
        dispatch.reports.setLoading(true);
        const response = await http.get(generatePath(URL.REPORT_PAGES, { cycleId }));
        if (response.status === 200) {
          dispatch.reports.setReports(response.data.reports);
          dispatch.reports.setMainScreenshot(response.data.mainScreen);
        } else {
          dispatch.reports.setError(response.data.message);
        }
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async fetchReportPages(cycleReportId: string) {
      try {
        dispatch.reports.setLoading(true);
        const response = await http.get(generatePath(URL.CYCLE_REPORT_PAGES, { cycleReportId }));
        if (response.status === 200) {
          dispatch.reports.setReports(response.data.reports);
          dispatch.reports.setPdfReports(response.data.pdfReports);
          dispatch.reports.setMainScreenshot(response.data.mainScreen);
        } else {
          dispatch.reports.setError(response.data.message);
        }
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async uploadAndSplitPdf({ file, cycleId }: { file: File; cycleId: string }) {
      try {
        dispatch.reports.setLoading(true);
        const formData = new FormData();
        formData.append('pdf', file);
        formData.append('cycleId', cycleId);

        const response = await http.post(URL.UPLOAD_AND_SPLIT_PDF, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        dispatch.reports.setReports(response.data.reports);
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async takeGraphScreenshot(data: ITakeGraphScreenshotProps) {
      const { cycleId, dataZoom, screenshot, previewScreenshot, timeLine, channels } = data;
      try {
        dispatch.reports.setLoading(true);
        const formData = new FormData();
        formData.append('cycleId', cycleId);
        formData.append('dataZoom', JSON.stringify(dataZoom));
        formData.append('timeLine', JSON.stringify(timeLine));
        formData.append('channels', JSON.stringify(channels));
        formData.append('screenshot', screenshot, 'screenshot.png');
        if (previewScreenshot) formData.append('previewScreenshot', previewScreenshot, 'previewScreenshot.png');

        const response = await http.post(generatePath(URL.TAKE_GRAPH_SCREENSHOT), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        dispatch.reports.setReports(response.data);
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async takeMainGraphScreenshot(data: ITakeMainScreenshotProps) {
      const { cycleId, screenshot } = data;
      try {
        dispatch.reports.setMainScreenLoading(true);
        const formData = new FormData();
        formData.append('cycleId', cycleId);
        formData.append('screenshot', screenshot, 'screenshot.png');

        const response = await http.post(generatePath(URL.TAKE_MAIN_SCREENSHOT), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        dispatch.reports.setReports(response.data);
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setMainScreenLoading(false);
      }
    },

    async generatePdf({ cycleId, skipCalCertCheck }: { cycleId: string; skipCalCertCheck?: boolean }) {
      try {
        dispatch.reports.setDownloading(true);
        const response = await http.get(generatePath(URL.GENERATE_PDF), {
          params: { cycleId, skipCalCertCheck },
          responseType: 'blob',
        });
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const filename = getFilenameFromHeaders(response.headers);

        saveAs(pdfBlob, filename);
      } catch (error) {
        await parseBlobErrorResponse(error);
        dispatch.reports.setError(error.message);
        throw error;
      } finally {
        dispatch.reports.setDownloading(false);
      }
    },

    async generatePdfV2({ cycleId, skipCalCertCheck }: { cycleId: string; skipCalCertCheck?: boolean }) {
      try {
        dispatch.reports.setDownloading(true);
        const response = await apiService.api.get(generatePath(URL.GENERATE_PDF), {
          headers,
          params: { cycleId, skipCalCertCheck },
          responseType: 'blob',
        });
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const filename = getFilenameFromHeaders(response.headers);

        saveAs(pdfBlob, filename);
      } catch (error) {
        await parseBlobErrorResponse(error);
        dispatch.reports.setError(error.message);
        throw error;
      } finally {
        dispatch.reports.setDownloading(false);
      }
    },

    async updateReportPositions(reports) {
      try {
        const reportsWithIds = reports.map((report, index) => ({
          id: report.id,
          position: index,
        }));

        await http.put(generatePath(URL.UPDATE_REPORT_POSITIONS), {
          reports: reportsWithIds,
        });
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async updateReportInclude({ reportId, isIncluded }: { reportId: string; isIncluded: boolean }) {
      try {
        await http.put(generatePath(URL.UPDATE_REPORT_INCLUDED), {
          reportId,
          isIncluded,
        });
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async updateReportPositionsV2(reports) {
      try {
        const reportsWithIds = reports.map((report, index) => ({
          id: report.id,
          position: index,
        }));

        await apiService.api.put(
          generatePath(URL.UPDATE_REPORT_POSITIONS),
          {
            reports: reportsWithIds,
          },
          { headers },
        );
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async updateReportIncludeV2({ reportId, isIncluded }: { reportId: string; isIncluded: boolean }) {
      try {
        await apiService.api.put(
          generatePath(URL.UPDATE_REPORT_INCLUDED),
          {
            reportId,
            isIncluded,
          },
          { headers },
        );
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async getCustomChannelNames(cycleId: string) {
      try {
        const response = await http.post(generatePath(URL.CUSTOM_CHANNEL_NAMES), { cycleId }, { headers });
        return response.data;
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async saveCustomChannelNames({
      cycleId,
      customChannelNames,
    }: {
      cycleId: string;
      customChannelNames: ICustomChannelName[];
    }) {
      try {
        await http.post(generatePath(URL.SAVE_CUSTOM_CHANNEL_NAMES), { cycleId, customChannelNames });
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },

    async getCycleReportItemsV2(cycleId: string) {
      try {
        dispatch.reports.setLoading(true);

        const path = generatePath(URL.REPORT_CYCLE_ITEMS, { cycleId });
        const response = await http.get(path, { headers });

        if (response.status === 200) {
          dispatch.reports.setReportItems(response.data);
          return response.data;
        } else {
          dispatch.reports.setError(response.data.message);
        }
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async updateCycleReportItemsV2(payload: {
      cycleId: string;
      items: { id: string; color: string; excluded: boolean }[];
    }) {
      try {
        const { cycleId, items } = payload;
        dispatch.reports.setLoading(true);

        const path = generatePath(URL.REPORT_CYCLE_ITEMS, { cycleId });

        await http.patch(path, items, { headers });
        toast.success('Chart settings updated successfully');
      } catch (error) {
        dispatch.reports.setError(error.message);
      } finally {
        dispatch.reports.setLoading(false);
      }
    },

    async deleteReport(
      {
        reportId,
        cycleId,
      }: {
        cycleId: string;
        reportId: string;
      },
      state,
    ) {
      try {
        const path = generatePath(URL.DELETE_REPORT_CYCLE, { id: reportId, cycleId });
        await http.delete(path);

        const filteredReports = state.reports.reports.filter((report) => report.id !== reportId);

        dispatch.reports.setReports(filteredReports);
      } catch (error) {
        dispatch.reports.setError(error.message);
      }
    },
  }),
});
